// Deemed Cancellation config + Legal Notice template — Phase 5.
// Reuses the existing reminder_master + EmailSmsTemplates tables (project_id/wing_id columns
// added to reminder_master) instead of dedicated DeemedCancellationConfig/DeemedNoticeTemplate tables.
//
// Mapping:
//   - DC trigger day (window+grace)  -> reminder_master row, reminder_type="deemed_legal_notice", day_offset
//   - Legal Notice content           -> EmailSmsTemplates row referenced by that row's template_id
//   - Release day + enabled flag     -> reminder_master row, reminder_type="deemed_release", day_offset + is_active
//   - Pre-DC reminder days           -> one reminder_master row PER DAY, reminder_type="pre_dc_reminder",
//     each with its OWN EmailSmsTemplates row (own subject/body) — not a shared placeholder.
import { supabase } from "../supabaseClient";

const RM_TABLE = "reminder_master";
const TPL_TABLE = "EmailSmsTemplates";
const LEGAL_NOTICE_TYPE = "deemed_legal_notice";
const RELEASE_TYPE = "deemed_release";
const PRE_DC_TYPE = "pre_dc_reminder";
const ATTACHMENT_BUCKET = "AllotmentLetter"; // reuse existing bucket; files under deemed_reminder_attachments/

const now = () => Date.now();

// Optional attachment for a single Pre-DC reminder day. Not mandatory — callers just don't call
// this if no file was picked, and attachmentUrl stays null/unchanged.
async function uploadReminderAttachment(projectId, day, file) {
  const path = `deemed_reminder_attachments/${projectId}_day${day}_${file?.name || "attachment"}`;
  const { error } = await supabase.storage.from(ATTACHMENT_BUCKET).upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from(ATTACHMENT_BUCKET).getPublicUrl(path);
  return data?.publicURL || null;
}

async function borrowPlaceholderTemplateId(preferredId) {
  if (preferredId) return preferredId;
  const { data } = await supabase.from(TPL_TABLE).select("id").limit(1);
  const id = data?.[0]?.id;
  if (!id) throw new Error("No EmailSmsTemplates row exists yet — create at least one template before saving deemed-cancellation config.");
  return id;
}

/** Loads all deemed-cancellation config for a project (+ optional wing) from reminder_master,
 * including each Pre-DC reminder day's OWN email content. */
async function getConfigForProject(projectId, wingId = null) {
  const { data, error } = await supabase
    .from(RM_TABLE)
    .select("id, reminder_type, day_offset, template_id, is_active, project_id, wing_id")
    .eq("project_id", projectId)
    .in("reminder_type", [LEGAL_NOTICE_TYPE, RELEASE_TYPE, PRE_DC_TYPE]);
  if (error) throw error;

  const rows = (data || []).filter((r) => (wingId ? r.wing_id === wingId : !r.wing_id));
  const legalNoticeRow = rows.find((r) => r.reminder_type === LEGAL_NOTICE_TYPE) || null;
  const releaseRow = rows.find((r) => r.reminder_type === RELEASE_TYPE) || null;
  const reminderRowsRaw = rows.filter((r) => r.reminder_type === PRE_DC_TYPE).sort((a, b) => a.day_offset - b.day_offset);

  let notice = null;
  if (legalNoticeRow?.template_id) {
    const { data: tpl } = await supabase.from(TPL_TABLE).select("*").eq("id", legalNoticeRow.template_id).maybeSingle();
    notice = tpl;
  }

  const templateIds = [...new Set(reminderRowsRaw.map((r) => r.template_id).filter(Boolean))];
  let templatesById = new Map();
  if (templateIds.length) {
    const { data: tpls } = await supabase.from(TPL_TABLE).select("id, emailSubject, emailTemplate, attachmentUrl").in("id", templateIds);
    templatesById = new Map((tpls || []).map((t) => [t.id, t]));
  }
  const reminderRows = reminderRowsRaw.map((r) => ({
    id: r.id,
    day: r.day_offset,
    templateId: r.template_id,
    emailSubject: templatesById.get(r.template_id)?.emailSubject || "",
    emailTemplate: templatesById.get(r.template_id)?.emailTemplate || "",
    attachmentUrl: templatesById.get(r.template_id)?.attachmentUrl || null,
  }));

  return {
    triggerDay: legalNoticeRow?.day_offset ?? "",
    legalNoticeRowId: legalNoticeRow?.id || null,
    legalNoticeTemplateId: legalNoticeRow?.template_id || null,
    releaseDay: releaseRow?.day_offset ?? "",
    releaseRowId: releaseRow?.id || null,
    enabled: releaseRow ? releaseRow.is_active !== false : true,
    reminderRows, // [{id, day, templateId, emailSubject, emailTemplate}]
    notice,
  };
}

/** Saves trigger day / release day / enabled. Does NOT touch reminder days or Legal Notice content. */
async function saveSettings({
  projectId, wingId = null, triggerDay, releaseDay, enabled,
  legalNoticeRowId, legalNoticeTemplateId, releaseRowId,
}) {
  // No hardcoded day defaults — both must be real, explicit positive numbers.
  const triggerDayNum = Number(triggerDay);
  const releaseDayNum = Number(releaseDay);
  if (!Number.isInteger(triggerDayNum) || triggerDayNum <= 0) throw new Error("DC Trigger Day is required and must be a positive whole number.");
  if (!Number.isInteger(releaseDayNum) || releaseDayNum <= 0) throw new Error("Release Day is required and must be a positive whole number.");

  const placeholderTemplateId = await borrowPlaceholderTemplateId(legalNoticeTemplateId);

  // Legal-notice row: keep its existing template if any, just update the trigger day.
  const legalNoticePayload = {
    reminder_type: LEGAL_NOTICE_TYPE, recipient: "customer",
    day_offset: triggerDayNum,
    template_id: legalNoticeTemplateId || placeholderTemplateId,
    is_active: true, project_id: projectId, wing_id: wingId,
  };
  if (legalNoticeRowId) {
    const { error } = await supabase.from(RM_TABLE).update(legalNoticePayload).eq("id", legalNoticeRowId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from(RM_TABLE).insert([legalNoticePayload]);
    if (error) throw error;
  }

  // Release row: day + the single enabled/disabled switch for this project.
  const releasePayload = {
    reminder_type: RELEASE_TYPE, recipient: "customer",
    day_offset: releaseDayNum,
    template_id: placeholderTemplateId,
    is_active: enabled !== false, project_id: projectId, wing_id: wingId,
  };
  if (releaseRowId) {
    const { error } = await supabase.from(RM_TABLE).update(releasePayload).eq("id", releaseRowId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from(RM_TABLE).insert([releasePayload]);
    if (error) throw error;
  }

  return getConfigForProject(projectId, wingId);
}

/** Saves the Legal Notice email content. Creates the deemed_legal_notice row if it doesn't exist yet (requires a valid triggerDay — no hardcoded default). */
async function saveNoticeTemplate({
  projectId, wingId = null, emailSubject, emailTemplate,
  legalNoticeRowId, legalNoticeTemplateId, triggerDay,
}) {
  let templateId = legalNoticeTemplateId;
  if (templateId) {
    const { error } = await supabase.from(TPL_TABLE)
      .update({ emailSubject, emailTemplate, last_updated_at: now() }).eq("id", templateId);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from(TPL_TABLE)
      .insert([{ type: LEGAL_NOTICE_TYPE, emailSubject, emailTemplate }]).select();
    if (error) throw error;
    templateId = data?.[0]?.id;
  }

  const triggerDayNum = Number(triggerDay);
  if (!Number.isInteger(triggerDayNum) || triggerDayNum <= 0) throw new Error("DC Trigger Day is required and must be a positive whole number.");

  const legalNoticePayload = {
    reminder_type: LEGAL_NOTICE_TYPE, recipient: "customer",
    day_offset: triggerDayNum,
    template_id: templateId, is_active: true, project_id: projectId, wing_id: wingId,
  };
  if (legalNoticeRowId) {
    const { error } = await supabase.from(RM_TABLE).update(legalNoticePayload).eq("id", legalNoticeRowId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from(RM_TABLE).insert([legalNoticePayload]);
    if (error) throw error;
  }

  return getConfigForProject(projectId, wingId);
}

/** Saves ONE Pre-DC reminder day, with its OWN email content (own EmailSmsTemplates row —
 * never shared/borrowed). Pass `id`/`templateId` when editing an existing day, omit for a new one. */
async function saveReminderDay({
  projectId, wingId = null, id, templateId, day, emailSubject, emailTemplate, attachmentUrl,
}) {
  const dayNum = Number(day);
  if (!Number.isInteger(dayNum) || dayNum <= 0) throw new Error("Day must be a positive whole number.");
  if (!emailSubject?.trim()) throw new Error(`Day ${dayNum}: email subject is required.`);

  // Reject a duplicate day (a different row already using this day number).
  const { data: existing, error: readErr } = await supabase
    .from(RM_TABLE).select("id, wing_id, day_offset")
    .eq("project_id", projectId).eq("reminder_type", PRE_DC_TYPE).eq("day_offset", dayNum);
  if (readErr) throw readErr;
  const clash = (existing || []).find((r) => (wingId ? r.wing_id === wingId : !r.wing_id) && r.id !== id);
  if (clash) throw new Error(`Day ${dayNum} is already configured on another row.`);

  // attachmentUrl is optional — not required, and passing it through as-is (including null/undefined)
  // lets a day be saved with no attachment at all, same as before this field existed.
  let tplId = templateId;
  if (tplId) {
    const { error } = await supabase.from(TPL_TABLE)
      .update({ emailSubject, emailTemplate, attachmentUrl, last_updated_at: now() }).eq("id", tplId);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from(TPL_TABLE)
      .insert([{ type: PRE_DC_TYPE, emailSubject, emailTemplate, attachmentUrl }]).select();
    if (error) throw error;
    tplId = data?.[0]?.id;
  }

  const payload = {
    reminder_type: PRE_DC_TYPE, recipient: "customer", day_offset: dayNum,
    template_id: tplId, is_active: true, project_id: projectId, wing_id: wingId,
  };
  if (id) {
    const { error } = await supabase.from(RM_TABLE).update(payload).eq("id", id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from(RM_TABLE).insert([payload]);
    if (error) throw error;
  }

  return getConfigForProject(projectId, wingId);
}

/** Removes ONE Pre-DC reminder day row (its EmailSmsTemplates row is left alone). */
async function removeReminderDay(rowId, projectId, wingId = null) {
  const { error } = await supabase.from(RM_TABLE).delete().eq("id", rowId);
  if (error) throw error;
  return getConfigForProject(projectId, wingId);
}

/** Deletes ALL saved deemed-cancellation config for a project (+ wing): trigger day, release
 * day/enabled, and every pre-DC reminder day row. Does NOT delete any EmailSmsTemplates rows
 * (they may be reused/borrowed elsewhere) — only the reminder_master rows. */
async function deleteConfig({ projectId, wingId = null, legalNoticeRowId, releaseRowId, reminderRowIds }) {
  const ids = [legalNoticeRowId, releaseRowId, ...(reminderRowIds || [])].filter(Boolean);
  if (!ids.length) return getConfigForProject(projectId, wingId);
  const { error } = await supabase.from(RM_TABLE).delete().in("id", ids);
  if (error) throw error;
  return getConfigForProject(projectId, wingId);
}

export {
  getConfigForProject, saveSettings, saveNoticeTemplate,
  saveReminderDay, removeReminderDay, deleteConfig, uploadReminderAttachment,
};
